import { createAsyncThunk } from '@reduxjs/toolkit';
import { getNerobottiThread } from '../services/getNerobottiThread';

/**
 * Ask whether the turn the socket died under finished anyway.
 *
 * The worker keeps generating after the client goes away — the round trip is
 * billed either way — and since it learned to file what it finished, a reply
 * the user never saw can be sitting in the thread by the time they are back.
 * Nothing was asking for it: a reconnect restores the socket and flushes the
 * send queue, and the panel goes on showing the "yhteys katkesi" line under a
 * question whose answer is already on the server. So the user asks again, and
 * pays for the same tool calls twice.
 *
 * ## Why this is not `openNerobottiThread`
 *
 * That thunk fetches the same transcript from the same endpoint, and reusing it
 * would have been one line. But its reducer *replaces* the screen — discussion,
 * `view`, `isAwaitingReply`, the lot. That is right for a conversation the user
 * picked out of the history list, and wrong here, where the fetch is
 * speculative and usually comes back with nothing new: a drop before the first
 * model call files no turn at all, which is the common shape now that the
 * worker checks the socket before it starts. Blanking a conversation to redraw
 * it identically — or worse, wiping the line that explains what happened — is
 * not something a background request may do. So the request is shared and the
 * reducer is not.
 *
 * The turn travels in as the argument rather than being read out of the store,
 * which keeps this file from importing the slice that imports it.
 */
export const recoverAbandonedTurn = createAsyncThunk(
    'nerobotti/recoverAbandonedTurn',
    async ({ threadId, question }) => {
        const data = await getNerobottiThread(threadId);

        return {
            threadId,
            // Echoed back so the reducer can tell an answer to *this* question
            // from the one already on screen — see the fulfilled case.
            question,
            turns: Array.isArray(data?.turns) ? data.turns : [],
            truncated: Boolean(data?.truncated),
        };
    }
);

export default recoverAbandonedTurn;
